export interface Tokens {
  accessToken: string;
  refreshToken: string;
}

const STORAGE_KEY = "counselor_auth_tokens";
const AUTH_TOKEN_KEY = "auth_token"; // For backward compatibility

// Emit custom events for token state changes
function emitTokenEvent(eventName: string, detail?: any) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  }
}

export function saveTokens(tokens: Tokens): void {
  // Any ordinary login/refresh ends a leftover impersonation marker (the
  // /impersonate page sets its own marker again right after calling this).
  localStorage.removeItem("gyanplug_impersonation");
  // Save in new format
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
  // Also save access token in old format for backward compatibility
  localStorage.setItem(AUTH_TOKEN_KEY, tokens.accessToken);
  emitTokenEvent('tokensSaved', { tokens });
}

export function getTokens(): Tokens | null {
  // First try to get from new format
  const json = localStorage.getItem(STORAGE_KEY);
  if (json) {
    try {
      return JSON.parse(json) as Tokens;
    } catch {
      // Invalid JSON, continue to fallback
    }
  }
  
  // Fallback to old format (just access token)
  const accessToken = localStorage.getItem(AUTH_TOKEN_KEY);
  if (accessToken) {
    // We don't have refresh token in old format, return null
    // This will trigger a re-login when refresh is needed
    return null;
  }
  
  return null;
}

export function getAccessToken(): string | null {
  const tokens = getTokens();
  if (tokens) {
    return tokens.accessToken;
  }
  // Fallback to old format
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(AUTH_TOKEN_KEY); // Also clear old format
  emitTokenEvent('tokensCleared');
}

// Helper to migrate from old format to new format
export function migrateTokens(accessToken: string, refreshToken: string): void {
  saveTokens({ accessToken, refreshToken });
}